import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Plus, Trash2, ToggleLeft, ToggleRight, ScrollText } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useDealerKitRegistrations,
  useCreateDealerKitRegistration,
  useUpdateDealerKitRegistration,
  useDeleteDealerKitRegistration,
  useDealerKitWebhookLogs,
} from '@/hooks/useDealerKit';
import { apiClient } from '@/lib/api/axios';

import type { WorkflowEntity } from '@repo/shared-types';

function useWorkflows() {
  return useQuery({
    queryKey: ['workflows'],
    queryFn: async () => {
      const res = await apiClient.get<WorkflowEntity[]>('/workflows');
      return res.data;
    },
  });
}

const LOG_STATUS_CLASS: Record<string, string> = {
  PROCESSED: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  FAILED: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  RECEIVED: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

function WebhookLogsDialog({
  registrationId,
  open,
  onOpenChange,
}: {
  registrationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading } = useDealerKitWebhookLogs(registrationId, 1, 20);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px]">
        <DialogHeader>
          <DialogTitle>Webhook Logs</DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] space-y-3 overflow-auto">
          {isLoading ? (
            <p className="text-muted-foreground text-sm">Loading...</p>
          ) : !data?.data.length ? (
            <p className="text-muted-foreground text-sm">No webhook events received yet.</p>
          ) : (
            data.data.map((log) => (
              <div key={log.id} className="rounded-md border p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground text-xs">
                    {new Date(log.created_at).toLocaleString('en-GB')}
                  </span>
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      LOG_STATUS_CLASS[log.status] ?? 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {log.status}
                  </span>
                </div>
                {log.error_message && (
                  <p className="text-destructive mt-1 text-xs">{log.error_message}</p>
                )}
                <pre className="bg-muted mt-2 max-h-60 overflow-auto rounded p-2 font-mono text-xs">
                  {JSON.stringify(log.payload, null, 2)}
                </pre>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const DealerKitRegistrationsPage = () => {
  const { data: registrations, isLoading } = useDealerKitRegistrations();
  const { data: workflows } = useWorkflows();
  const createMutation = useCreateDealerKitRegistration();
  const updateMutation = useUpdateDealerKitRegistration();
  const deleteMutation = useDeleteDealerKitRegistration();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [newType, setNewType] = useState<string>('lead');
  const [newWorkflowId, setNewWorkflowId] = useState<string | null>(null);
  const [logsRegId, setLogsRegId] = useState<string | null>(null);

  const handleCreate = async () => {
    await createMutation.mutateAsync({
      type: newType as 'lead' | 'stock' | 'dealer',
      workflow_id: newWorkflowId,
    });
    setDialogOpen(false);
    setNewType('lead');
    setNewWorkflowId(null);
  };

  const handleToggle = (id: string, currentEnabled: boolean) => {
    updateMutation.mutate({ id, data: { enabled: !currentEnabled } });
  };

  const handleDelete = (id: string) => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Delete this registration? This will also remove it from DealerKit.')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-foreground text-2xl font-bold">DealerKit Registrations</h1>
          <p className="text-muted-foreground text-sm">
            Manage webhook registrations with DealerKit
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Registration
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New DealerKit Registration</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <label className="text-sm font-medium">Type</label>
                <Select value={newType} onValueChange={setNewType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lead">Lead</SelectItem>
                    <SelectItem value="stock">Stock</SelectItem>
                    <SelectItem value="dealer">Dealer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Workflow</label>
                <Select
                  value={newWorkflowId ?? 'none'}
                  onValueChange={(v) => setNewWorkflowId(v === 'none' ? null : v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="No workflow" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No workflow</SelectItem>
                    {workflows
                      ?.filter(
                        (w) =>
                          w.trigger_type === 'dealerkit' ||
                          w.trigger_type === 'any' ||
                          !w.trigger_type,
                      )
                      .map((w) => (
                        <SelectItem key={w.id} value={w.id}>
                          {w.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleCreate} disabled={createMutation.isPending} className="w-full">
                {createMutation.isPending ? 'Creating...' : 'Create Registration'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : !registrations?.length ? (
        <p className="text-muted-foreground">No registrations yet.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>DealerKit ID</TableHead>
              <TableHead>Workflow</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {registrations.map((reg) => (
              <TableRow key={reg.id}>
                <TableCell className="capitalize">{reg.type}</TableCell>
                <TableCell>{reg.dealerkit_id}</TableCell>
                <TableCell>{reg.workflow_name ?? '—'}</TableCell>
                <TableCell>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                      reg.enabled
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}
                  >
                    {reg.enabled ? 'Active' : 'Disabled'}
                  </span>
                </TableCell>
                <TableCell>{new Date(reg.created_at).toLocaleDateString()}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setLogsRegId(reg.id)}
                      className="text-muted-foreground hover:text-foreground rounded p-1"
                      title="View webhook logs & payloads"
                    >
                      <ScrollText className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleToggle(reg.id, reg.enabled)}
                      className="text-muted-foreground hover:text-foreground rounded p-1"
                      title={reg.enabled ? 'Disable' : 'Enable'}
                    >
                      {reg.enabled ? (
                        <ToggleRight className="h-4 w-4" />
                      ) : (
                        <ToggleLeft className="h-4 w-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(reg.id)}
                      className="text-muted-foreground hover:text-destructive rounded p-1"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {logsRegId && (
        <WebhookLogsDialog
          registrationId={logsRegId}
          open={!!logsRegId}
          onOpenChange={(o) => !o && setLogsRegId(null)}
        />
      )}
    </div>
  );
};
